import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { leadsApi, importExportApi } from '../api';
import { Plus, Pencil, Trash2, UserCheck, Upload, Download } from 'lucide-react';
import LeadModal from '../components/LeadModal';
import ImportModal from '../components/ImportModal';

const STAGE_LABELS: Record<string, string> = {
  NEW: 'Новый', IN_WORK: 'В работе', QUALIFIED: 'Квалифицирован',
  CONVERTED: 'Конвертирован', REJECTED: 'Отклонен',
};

export default function LeadsPage() {
  const [leads, setLeads] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [page, setPage] = useState(1);
  const limit = 20;

  const load = async () => {
    setLoading(true);
    try {
      const res = await leadsApi.list({ search: search || undefined, page, limit });
      setLeads(res.data.data);
      setTotal(res.data.total);
    } catch (e) {
      console.error('Load leads error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search, page]);
  useEffect(() => { setPage(1); }, [search]);

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить лид?')) return;
    try { await leadsApi.remove(id); load(); } catch (e) { alert('Ошибка удаления'); }
  };

  const handleConvert = async (id: string) => {
    if (!confirm('Конвертировать лид в компанию, контакт и сделку?')) return;
    try {
      await leadsApi.convert(id);
      alert('Лид успешно конвертирован!');
      load();
    } catch (e) { alert('Ошибка конвертации'); }
  };

  const handleExport = async () => {
    try {
      const res = await importExportApi.exportLeads();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const d = new Date();
      a.download = `leads_${d.toISOString().slice(0, 10)}_${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Ошибка экспорта');
    }
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      NEW: 'badge-info', IN_WORK: 'badge-warning', QUALIFIED: 'badge-success',
      CONVERTED: 'badge-success', REJECTED: 'badge-danger',
    };
    return <span className={`badge ${colors[status] || 'badge-default'}`}>{STAGE_LABELS[status] || status}</span>;
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Лиды</h2>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-secondary" onClick={() => setShowImport(true)}>
            <Upload size={18} /> Импорт
          </button>
          <button className="btn btn-secondary" onClick={handleExport}>
            <Download size={18} /> Экспорт
          </button>
          <button className="btn btn-primary" onClick={() => { setEditing(null); setShowModal(true); }}>
            <Plus size={18} /> Добавить лид
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="toolbar" style={{ margin: 0, width: '100%' }}>
            <input className="search-input" placeholder="Поиск по лидам..."
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? <div style={{ padding: 40, textAlign: 'center' }}>Загрузка...</div> : (
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>ФИО</th><th>Email</th><th>Телефон</th><th>Компания</th><th>Статус</th><th>Источник</th><th>Действия</th></tr></thead>
                <tbody>
                  {leads.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-secondary)' }}>Лиды не найдены</td></tr>}
                  {leads.map(l => (
                    <tr key={l.id}>
                      <td style={{ fontWeight: 600 }}><Link to={`/leads/${l.id}`} className="detail-link">{l.firstName} {l.lastName}</Link></td>
                      <td className="text-muted">{l.email || '—'}</td>
                      <td>{l.phone || '—'}</td>
                      <td>{l.companyName || '—'}</td>
                      <td>{statusBadge(l.status)}</td>
                      <td><span className="badge badge-default">{l.source || '—'}</span></td>
                      <td>
                        <div className="flex gap-2">
                          <button className="header-icon-btn" style={{ width: 32, height: 32 }} onClick={() => { setEditing(l); setShowModal(true); }}>
                            <Pencil size={14} />
                          </button>
                          {l.status !== 'CONVERTED' && (
                            <button className="header-icon-btn" style={{ width: 32, height: 32, color: 'var(--color-success)' }} onClick={() => handleConvert(l.id)} title="Конвертировать">
                              <UserCheck size={14} />
                            </button>
                          )}
                          <button className="header-icon-btn" style={{ width: 32, height: 32, color: 'var(--color-danger)' }} onClick={() => handleDelete(l.id)}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="card-footer" style={{ padding: '16px 24px', borderTop: '1px solid var(--color-border-soft)' }}>
          <div className="pagination">
            <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Назад</button>
            <div className="flex gap-2" style={{ margin: '0 16px' }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Страница {page}</span>
            </div>
            <button className="btn btn-secondary btn-sm" disabled={page * limit >= total} onClick={() => setPage(p => p + 1)}>Далее</button>
          </div>
        </div>
      </div>

      {showModal && (
        <LeadModal lead={editing} onClose={() => setShowModal(false)} onSave={() => { setShowModal(false); load(); }} />
      )}

      {showImport && (
        <ImportModal
          entityType="leads"
          onImport={(file) => importExportApi.importLeads(file)}
          onClose={() => { setShowImport(false); load(); }}
        />
      )}
    </div>
  );
}
